import React, { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

function getCSRFToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith(name + "=")) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }
  return null;
}

const Client = () => {
  const [tipo, setTipo] = useState("N");
  const [esCliente, setEsCliente] = useState(true);
  const [esProveedor, setEsProveedor] = useState(false);
  const [esEmpleado, setEsEmpleado] = useState(false);
  const [esExtranjero, setEsExtranjero] = useState(false);

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, type: "error", message: "" });

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const data = {
      tipo,
      razon_social: formData.get("razon_social")?.toUpperCase().trim() || "",
      nombre_comercial: formData.get("nombre_comercial")?.toUpperCase().trim() || "",
      cedula: formData.get("cedula")?.trim() || "",
      ruc: formData.get("ruc")?.trim() || "",
      telefono: formData.get("telefono")?.trim() || "",
      correo: formData.get("correo")?.trim() || "",
      direccion: formData.get("direccion")?.trim() || "",
      es_cliente: esCliente,
      es_proveedor: esProveedor,
      es_empleado: esEmpleado,
      es_extranjero: esExtranjero,
    };

    try {
      const res = await fetch("/clientes/crear/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRFToken(),
        },
        body: JSON.stringify(data),
      });

      if (res.ok) {
        e.target.reset();
        navigate("/admin-panel/clientes", {
          state: { snackbar: { type: "success", message: "Persona registrada correctamente." } },
          replace: true,
        });
      } else {
        let msg = "Error al guardar la persona.";
        try {
          const err = await res.json();
          if (err?.error) msg = err.error;
        } catch {}
        setSnackbar({ open: true, type: "error", message: msg });
      }
    } catch {
      setSnackbar({ open: true, type: "error", message: "No se pudo conectar con el servidor." });
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    } catch {}
    localStorage.clear();
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#edf0f7ff" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />
      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />
        <Box sx={{ width: "90%", mx: "auto", mt: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              sx={{ textTransform: "none", bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" } }}
              onClick={() => navigate(-1)}
            >
              Regresar
            </Button>
          </Box>

          <Paper sx={{ p: 4, maxWidth: 720, margin: "0 auto" }}>
            <Typography variant="h5" mb={3}>
              Registrar Persona
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Persona</InputLabel>
                    <Select value={tipo} label="Tipo de Persona" onChange={(e) => setTipo(e.target.value)}>
                      <MenuItem value="N">Natural</MenuItem>
                      <MenuItem value="J">Jurídica</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormGroup row>
                    <FormControlLabel
                      control={<Checkbox checked={esCliente} onChange={(e) => setEsCliente(e.target.checked)} />}
                      label="Cliente"
                    />
                    <FormControlLabel
                      control={<Checkbox checked={esProveedor} onChange={(e) => setEsProveedor(e.target.checked)} />}
                      label="Proveedor"
                    />
                    <FormControlLabel
                      control={<Checkbox checked={esEmpleado} onChange={(e) => setEsEmpleado(e.target.checked)} />}
                      label="Empleado"
                    />
                    <FormControlLabel
                      control={<Checkbox checked={esExtranjero} onChange={(e) => setEsExtranjero(e.target.checked)} />}
                      label="Extranjero"
                    />
                  </FormGroup>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField label="Cédula" name="cedula" fullWidth />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField label="RUC" name="ruc" fullWidth />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label={tipo === "N" ? "Nombre completo" : "Razón Social"}
                    name="razon_social"
                    fullWidth
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField label="Nombre Comercial" name="nombre_comercial" fullWidth />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField label="Teléfono" name="telefono" fullWidth />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField label="Correo Electrónico" name="correo" type="email" fullWidth />
                </Grid>

                <Grid item xs={12}>
                  <TextField label="Dirección" name="direccion" multiline rows={3} fullWidth />
                </Grid>
              </Grid>

              <Box sx={{ textAlign: "right", mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ textTransform: "none", bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" } }}
                >
                  Guardar Persona
                </Button>
              </Box>
            </form>
          </Paper>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.type}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Client;
